import React, { useEffect, useState } from "react";
import { Download, Share, X, Plus } from "lucide-react";
import { isStandalone, platform } from "./lib/platform";

const DISMISS_KEY = "lc_m_install_dismissed_v1";

export default function InstallBanner() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [showIOSSheet, setShowIOSSheet] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (isStandalone()) return undefined;
    if (localStorage.getItem(DISMISS_KEY)) return undefined;

    const plat = platform();
    if (plat === "ios") {
      // iOS Safari — no beforeinstallprompt, show the manual-instructions banner
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, "installed");
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIOSSheet(false);
  };

  const install = async () => {
    const plat = platform();
    if (plat === "ios") {
      setShowIOSSheet(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => null);
    setDeferred(null);
    setVisible(false);
    if (choice && choice.outcome !== "accepted") {
      // Allow one more surfacing later
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } else {
      localStorage.setItem(DISMISS_KEY, "installed");
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed left-3 right-3 z-40 bg-white border border-[var(--lc-ink)] rounded-xl shadow-xl flex items-center gap-3 p-3"
        style={{ bottom: "calc(76px + env(safe-area-inset-bottom))" }}
        data-testid="install-banner"
      >
        <div className="w-10 h-10 rounded-md bg-[var(--lc-ink)] grid place-items-center flex-shrink-0">
          <Download className="h-4 w-4 text-[var(--lc-cream)]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm leading-tight">Install LONGEV on your phone</div>
          <div className="text-[11px] text-[var(--lc-muted)] mt-0.5">
            Faster launches, push reminders, home-screen icon.
          </div>
        </div>
        <button
          onClick={install}
          className="px-3 py-2 bg-[var(--lc-ink)] text-white text-[10px] font-bold tracking-[0.14em] uppercase rounded-md"
          data-testid="install-banner-cta"
        >
          Install
        </button>
        <button
          onClick={dismiss}
          className="w-7 h-7 grid place-items-center text-[var(--lc-muted)] hover:text-[var(--lc-ink)]"
          aria-label="Dismiss"
          data-testid="install-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {showIOSSheet && (
        <div
          className="fixed inset-0 z-50 bg-[var(--lc-ink)]/40 backdrop-blur-sm grid place-items-end"
          onClick={dismiss}
          data-testid="install-ios-sheet"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full bg-white rounded-t-2xl p-6"
            style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}
          >
            <div className="w-10 h-1 rounded-full bg-[var(--lc-line)] mx-auto mb-4" />
            <div className="eyebrow text-[var(--lc-moss)]">Add LONGEV to your Home Screen</div>
            <h3 className="font-serif-display text-2xl mt-1">
              Three taps in Safari
            </h3>
            <ol className="mt-5 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[var(--lc-cream)] grid place-items-center flex-shrink-0 border border-[var(--lc-line)]">
                  <Share className="h-3.5 w-3.5" />
                </span>
                <span>
                  <strong>Tap the Share icon</strong> at the bottom of Safari
                  <span className="block text-xs text-[var(--lc-muted)] mt-0.5">
                    (the square with an upward arrow)
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[var(--lc-cream)] grid place-items-center flex-shrink-0 border border-[var(--lc-line)]">
                  <Plus className="h-3.5 w-3.5" />
                </span>
                <span>
                  Scroll and tap <strong>"Add to Home Screen"</strong>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-[var(--lc-cream)] grid place-items-center flex-shrink-0 border border-[var(--lc-line)] text-[11px] font-bold">
                  3
                </span>
                <span>
                  Tap <strong>Add</strong> — LONGEV lives on your home screen like any other app.
                </span>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-6 w-full px-5 py-3.5 bg-[var(--lc-ink)] text-[var(--lc-cream)] text-[12px] font-bold tracking-[0.18em] uppercase rounded-md"
              data-testid="install-ios-close"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
